import { useContext, useEffect, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { Alert, Anchor, Box, Loader, Portal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { PORTAL_IDS } from 'constants/portal';
import LazyLoader from 'components/LazyLoader';
import { useCubohActionBar } from 'hooks/useCubohActionBar';
import { useMenuManagement } from 'hooks/useMenuManagement';
import NotificationWrapper from 'primitives/NotificationWrapper/NotificationWrapper';

import { useGetActiveMenuBuild, useGetMenuBuildStatus } from 'api/menuUpload';
import MenuInformation from 'components/MenuManagement/MenuInformation';
import CubohMerchantSelector from 'components/CubohMerchantSelector/CubohMerchantSelector';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { triggerCacheIfNeeded } from 'helpers/menuManagement';
import { furthestBuildStatus } from 'containers/MenuUpload/helpers';
import { MenuProgressModal } from 'containers/MenuUpload/MenuProgressModal';
import {
  clearMenuBuildStorage,
  loadMenuBuildFromStorage,
  saveMenuBuildToStorage,
} from 'containers/MenuUpload/menuBuildStorage';
import { useReportMenuBuildOutcome } from 'containers/MenuUpload/useReportMenuBuildOutcome';
import { TERMINAL_BUILD_STATUSES } from 'containers/MenuUpload/types';
import { HeadquarterContext } from 'context/HeadquarterContext';

import { useMenuBuildNotifications } from '../hooks/useMenuBuildNotifications';
import { useMenuNotifications } from '../hooks/useMenuNotifications';
import { MenuActionsDropdown } from './MenuActionsDropdown';
import { BellIconMd, CheckCircleIconMd, WarningIconMd } from '@chownow/pantry/icons';

const MerchantContainer = LazyLoader(
  () => import('components/MenuManagement/MenuDisplay/MerchantContainer'),
);
const { MenuStatus } = MenuInformation;

export default function MenuDisplay() {
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const { state } = useLocation();
  const { singleSelectedMerchantID, singleSelectedCompanyID, merchantsForSelectedCompany } =
    useCubohActionBar();

  const {
    cubohMenus,
    getCubohMenus,
    fetchAllSupportedIntegrations,
    fetchAvailableClassifications,
    fetchMerchantIntegrations,
    createMenu,
    setCreateMenu,
    tempSortedMenus,
    setTempSortedMenus,
    sortedMenus,
    setSortedMenus,
    resetSingleMenuState,
  } = useMenuManagement();
  const { isSingleMerchantType } = useContext(HeadquarterContext);
  const { menuUploadWizard } = useFlags();
  const isMenuUploadEnabled = !!menuUploadWizard;

  // Menu build progress state (initialized from route state passed by upload wizard)
  const menuBuild = state?.menuBuild;
  const [buildJobId, setBuildJobId] = useState(() => menuBuild?.jobId ?? null);
  const [buildMenuName, setBuildMenuName] = useState(() => menuBuild?.menuName ?? '');
  const [buildCompanyId, setBuildCompanyId] = useState(() => menuBuild?.companyId ?? null);
  const [buildMerchantId, setBuildMerchantId] = useState(() => menuBuild?.merchantId ?? null);
  const [showBuildBanner, setShowBuildBanner] = useState(() => !!menuBuild);
  // Separate from buildMenuName so the success banner outlives the cleared build state.
  const [createdMenuName, setCreatedMenuName] = useState(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(!!menuBuild);
  const [statusBanner, setStatusBanner] = useState(null);

  useEffect(() => {
    if (menuBuild) {
      window.history.replaceState({}, '');
      saveMenuBuildToStorage({
        jobId: menuBuild.jobId,
        menuName: menuBuild.menuName,
        companyId: menuBuild.companyId,
        merchantId: menuBuild.merchantId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; menuBuild is from useLocation and replaceState doesn't update it
  }, []);

  // Recover in-progress build when page loads without route state.
  // The BE active-build endpoint is company-scoped; merchantId in the path is only for routing,
  // so any merchant from the company works when no specific merchant is selected.
  const activeBuildMerchantId =
    singleSelectedMerchantID ?? merchantsForSelectedCompany[0]?.id ?? null;
  const {
    activeBuild,
    isLoading: isActiveBuildLoading,
    refetch: refetchActiveBuild,
  } = useGetActiveMenuBuild({
    companyId:
      isMenuUploadEnabled && singleSelectedCompanyID ? Number(singleSelectedCompanyID) : null,
    merchantId: isMenuUploadEnabled && activeBuildMerchantId ? Number(activeBuildMerchantId) : null,
  });

  useEffect(() => {
    if (!activeBuild || buildJobId) return;
    const isSuccessTerminal =
      activeBuild.status === 'menu_inserted' || activeBuild.status === 'finished';
    if (!isSuccessTerminal) {
      setBuildJobId(activeBuild.jobId);
      setBuildMenuName(activeBuild.menuName);
      setBuildCompanyId(activeBuild.companyId);
      setBuildMerchantId(activeBuild.merchantId);
      setShowBuildBanner(true);
      updateStatus(activeBuild.status);
      saveMenuBuildToStorage({
        jobId: activeBuild.jobId,
        menuName: activeBuild.menuName,
        companyId: activeBuild.companyId,
        merchantId: activeBuild.merchantId,
      });
    }
  }, [activeBuild]); // eslint-disable-line react-hooks/exhaustive-deps -- only recover once when activeBuild first arrives; setState setters are stable

  // Fallback: recover from localStorage when the active-build endpoint returns nothing
  // (e.g. build already failed — BE only returns non-terminal builds).
  useEffect(() => {
    if (buildJobId || menuBuild || isActiveBuildLoading || activeBuild) return;
    const stored = loadMenuBuildFromStorage();
    if (stored?.jobId && String(stored.companyId) === String(singleSelectedCompanyID)) {
      setBuildJobId(stored.jobId);
      setBuildMenuName(stored.menuName);
      setBuildCompanyId(stored.companyId);
      setBuildMerchantId(stored.merchantId);
      setShowBuildBanner(true);
    }
  }, [isActiveBuildLoading]); // eslint-disable-line react-hooks/exhaustive-deps -- run once after active build query settles

  const [buildStatus, setBuildStatus] = useState(null);
  const [lastActiveStatus, setLastActiveStatus] = useState(null);

  const updateStatus = status => {
    setBuildStatus(prev => furthestBuildStatus(prev, status));
    if (status !== 'failed') {
      setLastActiveStatus(prev => furthestBuildStatus(prev, status));
    }
  };

  // One-time fetch of current build status on mount/recovery.
  // Pusher doesn't replay missed events, so we need the latest status from the API.
  const { buildStatusData, refetchBuildStatus } = useGetMenuBuildStatus({
    companyId: buildCompanyId,
    merchantId: buildMerchantId,
    jobId: buildJobId,
  });

  useEffect(() => {
    if (buildStatusData?.status) updateStatus(buildStatusData.status);
  }, [buildStatusData]); // eslint-disable-line react-hooks/exhaustive-deps -- sync once when API data arrives; updateStatus uses functional updaters

  useMenuBuildNotifications({
    companyId: buildCompanyId,
    merchantId: buildMerchantId,
    onStatusChange: updateStatus,
  });

  useReportMenuBuildOutcome({ buildStatusData, jobId: buildJobId });

  // getCubohMenus() is intentionally NOT called here —
  // useMenuNotifications handles MENU_IMPORT_SUCCESSFUL on the company channel,
  // which LIS fires on wizard build completion. Calling it here would double-refresh.
  useEffect(() => {
    if (!buildStatus) return;
    if (buildStatus === 'menu_inserted' || buildStatus === 'finished') {
      setShowBuildBanner(false);
      setCreatedMenuName(buildMenuName);
      clearMenuBuildStorage();
      refetchActiveBuild();
    } else if (buildStatus === 'failed') {
      refetchActiveBuild();
    } else {
      return;
    }
    void refetchBuildStatus();
  }, [buildStatus]); // eslint-disable-line react-hooks/exhaustive-deps -- reset banner on terminal status; refetchActiveBuild is stable (queryClient.invalidateQueries)

  useEffect(() => {
    const handleMenuDeleted = event => {
      setStatusBanner({
        variant: 'success',
        message: <span data-testid="menu-delete-success-notification">{event.detail.message}</span>,
      });
      getCubohMenus();
    };

    const handleMenuDeleteError = event => {
      setStatusBanner({
        variant: 'caution',
        message: <span data-testid="menu-delete-error-notification">{event.detail.message}</span>,
      });
    };

    window.addEventListener('menu-deleted', handleMenuDeleted);
    window.addEventListener('menu-delete-error', handleMenuDeleteError);
    return () => {
      window.removeEventListener('menu-deleted', handleMenuDeleted);
      window.removeEventListener('menu-delete-error', handleMenuDeleteError);
    };
  }, []);

  useMenuNotifications({
    companyId: singleSelectedCompanyID,
    // On the window-event path, message is a plain string (wrap it for the test id);
    // Pusher paths already pass fully-built, test-id-wrapped JSX.
    onSuccess: message => {
      setStatusBanner({
        variant: 'success',
        message:
          typeof message === 'string' ? (
            <span data-testid="menu-import-request-sent-notification">{message}</span>
          ) : (
            message
          ),
      });
      getCubohMenus();
    },
    onError: message => {
      setStatusBanner({
        variant: 'caution',
        message:
          typeof message === 'string' ? (
            <span data-testid="menu-import-request-error-notification">{message}</span>
          ) : (
            message
          ),
      });
    },
  });

  useEffect(() => {
    (async () => {
      setSortedMenus(null);
      await getCubohMenus();
      resetSingleMenuState();
      fetchAllSupportedIntegrations(singleSelectedCompanyID);
      fetchAvailableClassifications(singleSelectedCompanyID);
      if (singleSelectedMerchantID && singleSelectedCompanyID) {
        fetchMerchantIntegrations(singleSelectedCompanyID, singleSelectedMerchantID);
      }
    })();
  }, [getCubohMenus, singleSelectedCompanyID]);

  useEffect(() => {
    if (singleSelectedMerchantID) {
      if (cubohMenus?.data[singleSelectedMerchantID]) {
        setSortedMenus({
          [singleSelectedMerchantID]: cubohMenus.data[singleSelectedMerchantID],
        });
      } else setSortedMenus({ [singleSelectedMerchantID]: [] });
    } else if (cubohMenus?.data) {
      const menuStructure = {};
      merchantsForSelectedCompany.forEach(({ id }) => {
        menuStructure[id] = cubohMenus.data[id] ?? [];
      });
      setSortedMenus(menuStructure);
    }
  }, [
    cubohMenus.data,
    singleSelectedCompanyID,
    singleSelectedMerchantID,
    merchantsForSelectedCompany,
    setSortedMenus,
  ]);

  const isBuildActive =
    isActiveBuildLoading ||
    !!activeBuild ||
    (!!buildJobId && !TERMINAL_BUILD_STATUSES.includes(buildStatus));

  const createdMenuUuid = buildStatusData?.menuUuid ?? null;

  const handlePreviewCreatedMenu = async () => {
    // Warm the menu cache first, same as MenuPreviewButton.
    try {
      await triggerCacheIfNeeded({
        companyId: buildCompanyId,
        merchantId: buildMerchantId,
        menuId: createdMenuUuid,
      });
    } catch {
      // Preview works without the cache, so navigate anyway.
    }
    navigate(`preview?merchantId=${buildMerchantId}&menuId=${createdMenuUuid}`);
  };

  if (cubohMenus?.isLoading || cubohMenus?.error?.hasError) {
    return (
      <MenuInformation cubohMenus={cubohMenus}>
        <MenuStatus />
      </MenuInformation>
    );
  }

  if (!sortedMenus) {
    return <Loader />;
  }

  return (
    <MenuInformation
      sortedMenus={tempSortedMenus ?? sortedMenus}
      setTempSortedMenus={setTempSortedMenus}
      setSortedMenus={setSortedMenus}
      createMenu={createMenu}
      setCreateMenu={setCreateMenu}
    >
      <Box mt={{ base: 'xs2', md: 0 }}>
        <CubohMerchantSelector />
        {statusBanner && (
          <Portal target={`#${PORTAL_IDS.coverBanner}`}>
            <Box mt={{ base: 'sm', md: 'md' }}>
              <NotificationWrapper
                variant={statusBanner.variant}
                indicator
                onClose={() => setStatusBanner(null)}
              >
                {statusBanner.message}
              </NotificationWrapper>
            </Box>
          </Portal>
        )}
        {isSingleMerchantType && singleSelectedMerchantID && (
          <Portal target={`#${PORTAL_IDS.coverBtn}`}>
            <MenuActionsDropdown
              companyId={singleSelectedCompanyID}
              merchantId={singleSelectedMerchantID}
              isBuildActive={isBuildActive}
            />
          </Portal>
        )}
        <MerchantContainer
          isBuildActive={isBuildActive}
          banner={
            <>
              {showBuildBanner && (
                <Alert
                  variant={buildStatus === 'failed' ? 'caution' : 'gray'}
                  icon={buildStatus === 'failed' ? <WarningIconMd /> : <BellIconMd />}
                  styles={{ wrapper: { alignItems: 'center' } }}
                  // Only a failed build is dismissible; an in-progress one must stay put.
                  withCloseButton={buildStatus === 'failed'}
                  closeButtonLabel="Dismiss"
                  onClose={() => {
                    setShowBuildBanner(false);
                    clearMenuBuildStorage();
                  }}
                  data-testid="menu-build-failed-banner"
                >
                  {buildStatus === 'failed' ? (
                    <>
                      There was an error importing your menu file.{' '}
                      <Anchor component="button" onClick={openModal} size="sm">
                        Learn more
                      </Anchor>
                    </>
                  ) : (
                    <>
                      We&apos;re building &apos;{buildMenuName}&apos;. See progress{' '}
                      <Anchor component="button" onClick={openModal} size="sm">
                        here
                      </Anchor>
                      .
                    </>
                  )}
                </Alert>
              )}
              {createdMenuName && (
                <Alert
                  variant="success"
                  icon={<CheckCircleIconMd aria-hidden />}
                  styles={{ wrapper: { alignItems: 'center' } }}
                  withCloseButton
                  closeButtonLabel="Dismiss"
                  onClose={() => setCreatedMenuName(null)}
                  data-testid="menu-build-success-banner"
                >
                  &apos;{createdMenuName}&apos; has been created. Remember to publish your menu to
                  make it live.{' '}
                  {createdMenuUuid && (
                    <Anchor
                      component="button"
                      onClick={() => void handlePreviewCreatedMenu()}
                      size="sm"
                    >
                      Preview menu
                    </Anchor>
                  )}
                </Alert>
              )}
            </>
          }
        />
      </Box>
      {buildJobId && (
        <MenuProgressModal
          opened={modalOpened}
          onClose={closeModal}
          onDone={closeModal}
          onReUpload={() => {
            clearMenuBuildStorage();
            void navigate(`/restaurant/${restaurantId}/menu/upload`);
          }}
          onContactSupport={() => {
            setShowBuildBanner(false);
            clearMenuBuildStorage();
          }}
          menuName={buildMenuName}
          status={buildStatus}
          lastActiveStatus={lastActiveStatus}
        />
      )}
    </MenuInformation>
  );
}
